import React, { Fragment, memo, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Map } from 'immutable'
import { formatPrice } from '@aytm/app/swidget/questions/components/sh/functions'

import shelfStyle from '@aytm/app/swidget/questions/styles/sh/shelf.module.scss'

// Walmart's spark mark beside the Rollback caption
const SPARK_RAYS = [0, 60, 120, 180, 240, 300]

export function ItemPrice(props) {
  const { item, questionSchema, type, left, noRandom } = props
  if (!item.get('price')) return <Fragment />
  const price = item.get('price')
  const discount = item.get('discounted_price')
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const randomLeft = useMemo(() => (noRandom ? 0 : Math.random() * 5 - 5), [noRandom])
  const discounted = discount > 0
  const showLongPrices = 'custom' != questionSchema.get('shelf_type')
  // Walmart Rollback: the author's flag, not the discount, picks the frame, and the tag inside it has
  // a layout of its own (unit block, big price, "was" line) instead of the two-block discount tag.
  const rollback = 'walmart' === type && Boolean(item.get('rollback'))
  if (false === item.get('available')) return <Fragment />

  return (
    <div
      className={
        `${shelfStyle.price} ${shelfStyle.topItem} ${shelfStyle[questionSchema.get('store')]} ` +
        (rollback ? `${shelfStyle.rollback}` : discounted ? `${shelfStyle.discounted}` : `${shelfStyle.normal}`)
      }
      data-testid="SW-SH-Price"
      style={{
        marginLeft: -randomLeft + 'px',
        left: left ? left + 'px' : 'auto'
      }}
    >
      {
        {
          walmart: rollback ? (
            <div className={shelfStyle.rollbackFrame}>
              <div className={`${shelfStyle.rollbackTag} ${discounted ? '' : shelfStyle.rollbackOnly}`}>
                {discounted && (
                  <div
                    className={shelfStyle.rollbackUnit}
                    dangerouslySetInnerHTML={{ __html: formatPrice(discount, questionSchema.get('price_mask'), true) }}
                  />
                )}
                <div
                  className={shelfStyle.rollbackMain}
                  dangerouslySetInnerHTML={{
                    __html: formatPrice(discounted ? discount : price, questionSchema.get('price_mask'), true)
                  }}
                />
                {discounted && (
                  <div className={shelfStyle.rollbackWas}>
                    was:{' '}
                    <div
                      dangerouslySetInnerHTML={{ __html: formatPrice(price, questionSchema.get('price_mask'), true) }}
                    />
                  </div>
                )}
                <div className={shelfStyle.rollbackCode}>
                  <i />
                  <i />
                </div>
              </div>
              <div className={shelfStyle.rollbackLabel} data-testid="SW-SH-Rollback">
                <svg className={shelfStyle.rollbackSpark} viewBox="0 0 24 24" aria-hidden="true">
                  {SPARK_RAYS.map(angle => (
                    <rect
                      key={angle}
                      x="10.5"
                      y="1"
                      width="3"
                      height="8"
                      rx="1.5"
                      transform={`rotate(${angle} 12 12)`}
                    />
                  ))}
                </svg>
                Rollback
              </div>
            </div>
          ) : (
            <Fragment>
              <div className={shelfStyle.left}>
                <div
                  className={shelfStyle.top}
                  dangerouslySetInnerHTML={{
                    __html: discounted ? formatPrice(price, questionSchema.get('price_mask'), true) : ''
                  }}
                />
                <div className={shelfStyle.bottom} />
              </div>
              <div className={shelfStyle.right}>
                <div
                  className={shelfStyle.top}
                  dangerouslySetInnerHTML={{
                    __html: formatPrice(discounted ? discount : price, questionSchema.get('price_mask'), true)
                  }}
                />
                <div className={shelfStyle.bottom} />
              </div>
            </Fragment>
          ),
          target: (
            <Fragment>
              <div className={shelfStyle.normal}>
                <div className={shelfStyle.firstLine} />
                <div className={shelfStyle.secondLine} />
                <div className={shelfStyle.yellowLine}>
                  <span dangerouslySetInnerHTML={{ __html: formatPrice(price, questionSchema.get('price_mask')) }} />
                </div>
                <div className={shelfStyle.bottom}>
                  <div className={shelfStyle.left} />
                  <div className={shelfStyle.right} />
                </div>
              </div>
              {discounted && showLongPrices && (
                <div className={shelfStyle.discounted}>
                  <div className={shelfStyle.redLine}>
                    <span>Sale</span>
                    <div className={shelfStyle.whiteLine} />
                  </div>
                  <div className={shelfStyle.discountBlock}>
                    <div className={shelfStyle.redLine} />
                    <span
                      dangerouslySetInnerHTML={{ __html: formatPrice(discount, questionSchema.get('price_mask')) }}
                    />
                    <br />
                    <div className={shelfStyle.grayLines} />
                    <div className={shelfStyle.redCircle}>%</div>
                    <div className={shelfStyle.bottom} />
                  </div>
                </div>
              )}
            </Fragment>
          ),
          family: (
            <Fragment>
              <div>
                <div className={shelfStyle.blackLine} />
                <div className={shelfStyle.firstLine} />
                <div className={shelfStyle.secondLine} />
                <div className={shelfStyle.thirdLine} />
                <span
                  dangerouslySetInnerHTML={{
                    __html: formatPrice(price, questionSchema.get('price_mask')).replace('.', '')
                  }}
                />
              </div>
              {discounted && showLongPrices && (
                <div className={shelfStyle.discountBlock}>
                  <div className={shelfStyle.circle}>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: formatPrice(discount, questionSchema.get('price_mask')).replace('.', '')
                      }}
                    />
                  </div>
                  <div className={shelfStyle.wow}>WOW</div>
                </div>
              )}
            </Fragment>
          ),
          publix: (
            <Fragment>
              {discounted ? (
                <div>
                  <div className={shelfStyle.redLine}>
                    <div className={shelfStyle.whiteLine} />
                  </div>
                  <div className={shelfStyle.circle} />
                  <span dangerouslySetInnerHTML={{ __html: formatPrice(discount, questionSchema.get('price_mask')) }} />
                  <div className={shelfStyle.greenBox}>
                    <span>SAVE</span>
                  </div>
                  <div className={shelfStyle.secondLine} />
                  <div className={shelfStyle.thirdLine} />
                </div>
              ) : (
                <div>
                  <div className={shelfStyle.firstLine} />
                  <span dangerouslySetInnerHTML={{ __html: formatPrice(price, questionSchema.get('price_mask')) }} />
                  <div className={shelfStyle.grayLine1} />
                  <div className={shelfStyle.grayLine2} />
                  <div className={shelfStyle.grayLine3} />
                  <div className={shelfStyle.secondLine} />
                  <div className={shelfStyle.thirdLine} />
                </div>
              )}
            </Fragment>
          ),
          dollar: (
            <Fragment>
              <div className={shelfStyle.grayLine} />
              <div className={shelfStyle.grayLine2} />
              <div className={shelfStyle.grayLine3} />
              <div className={shelfStyle.bottomLine} />
              <div className={shelfStyle.bottomLine2} />
              <div className={shelfStyle.rightBlock}>
                <span
                  dangerouslySetInnerHTML={{
                    __html: formatPrice(discount ? discount : price, questionSchema.get('price_mask')).replace('.', '')
                  }}
                />
              </div>
            </Fragment>
          ),
          walgreens: (
            <Fragment>
              {discounted ? (
                <div className={shelfStyle.discounted}>
                  <div className={`${shelfStyle.discountBlock} ` + (showLongPrices ? '' : `${shelfStyle.short}`)}>
                    <span className={shelfStyle.sale}>
                      {' '}
                      sale <sub>this card</sub>{' '}
                    </span>
                    <div className={shelfStyle.grayLine} />
                    <span
                      dangerouslySetInnerHTML={{
                        __html: formatPrice(discount, questionSchema.get('price_mask')).replace('.', '')
                      }}
                    />
                    <div className={shelfStyle.grayLine2} />
                    <div className={shelfStyle.grayLine3} />
                    <div className={shelfStyle.grayLine4} />
                    <div className={shelfStyle.grayLine5} />
                  </div>
                </div>
              ) : (
                <div className={shelfStyle.normal}>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: formatPrice(price, questionSchema.get('price_mask')).replace('.', '')
                    }}
                  />
                  <div className={shelfStyle.leftLine} />
                  <div className={shelfStyle.grayLine} />
                  <div className={shelfStyle.rightLine1} />
                  <div className={shelfStyle.rightLine2} />
                  <div className={shelfStyle.bottomLine} />
                  <div className={shelfStyle.bottomLine2} />
                </div>
              )}
            </Fragment>
          ),
          amazon: (
            <Fragment>
              <div className={shelfStyle.leftLine} />
              <div className={shelfStyle.innerDiv}>
                <div className={shelfStyle.firstLine} />
                {discounted && <div className={shelfStyle.redLine} />}
                <div className={shelfStyle.starLine} />
                <span
                  dangerouslySetInnerHTML={{
                    __html: formatPrice(discounted ? discount : price, questionSchema.get('price_mask'))
                  }}
                />
                <div className={shelfStyle.secondLine} />
                <div className={shelfStyle.thirdLine} />
              </div>
              <div className={shelfStyle.rightLine} />
            </Fragment>
          ),
          costco: (
            <Fragment>
              <div className={shelfStyle.firstLine} />
              <div className={shelfStyle.secondLine} />
              <div className={shelfStyle.grayLine1} />
              <div className={shelfStyle.grayLine2} />
              <div className={shelfStyle.yellowLine} />
              <div className={shelfStyle.line3} />
              <span
                dangerouslySetInnerHTML={{
                  __html: formatPrice(discounted ? discount : price, questionSchema.get('price_mask'), true)
                }}
              />
            </Fragment>
          ),
          homedepot: (
            <Fragment>
              <div className={shelfStyle.firstLine} />
              <div className={shelfStyle.secondLine} />
              <div className={shelfStyle.thirdLine} />
              <div className={shelfStyle.grayLine1} />
              <div className={shelfStyle.grayLine2} />
              <div className={shelfStyle.grayLine3} />
              <div
                className={shelfStyle.text}
                dangerouslySetInnerHTML={{
                  __html: formatPrice(discounted ? discount : price, questionSchema.get('price_mask'))
                }}
              />
            </Fragment>
          ),
          samsclub: (
            <Fragment>
              <div className={shelfStyle.firstLine} />
              <div className={shelfStyle.secondLine} />
              <div className={shelfStyle.thirdLine} />
              <div className={shelfStyle.grayLine1} />
              <div className={shelfStyle.grayLine2} />
              <div
                className={shelfStyle.text}
                dangerouslySetInnerHTML={{
                  __html: formatPrice(discounted ? discount : price, questionSchema.get('price_mask'))
                }}
              />
            </Fragment>
          ),
          cvs: (
            <Fragment>
              <div className={shelfStyle.grayLine} />
              <div className={shelfStyle.leftLine} />
              <div className={shelfStyle.rightLine} />
              <div className={shelfStyle.blueLine} />
              <div className={shelfStyle.leftLine2} />
              <div
                className={shelfStyle.text}
                dangerouslySetInnerHTML={{ __html: formatPrice(price, questionSchema.get('price_mask')) }}
              />
              <div className={shelfStyle.leftLine3} />
              <div className={shelfStyle.leftLine4} />
              {discounted && showLongPrices && (
                <div className={shelfStyle.discounted}>
                  <div className={shelfStyle.topLine} />
                  <div className={shelfStyle.grayLine1} />
                  <div className={shelfStyle.grayLine2} />
                  <div className={shelfStyle.grayLine3} />
                  <div className={shelfStyle.grayLine4} />
                  <div className={shelfStyle.bottomLine} />
                  <div
                    className={shelfStyle.text}
                    dangerouslySetInnerHTML={{ __html: formatPrice(discount, questionSchema.get('price_mask')) }}
                  />
                  <div className={shelfStyle.bottomBlock}>
                    <div className={shelfStyle.blackLine} />
                  </div>
                </div>
              )}
            </Fragment>
          ),
          lowes: (
            <Fragment>
              {discounted ? (
                <div className={shelfStyle.discounted}>
                  <div className={shelfStyle.grayLine1} />
                  <div className={shelfStyle.grayLine2} />
                  <div className={shelfStyle.grayLine3} />
                  <div className={shelfStyle.grayLine4} />
                  <div className={shelfStyle.right}>
                    <div
                      className={shelfStyle.text}
                      dangerouslySetInnerHTML={{ __html: formatPrice(discount, questionSchema.get('price_mask')) }}
                    />
                    <div className={shelfStyle.grayLine1} />
                    <div className={shelfStyle.grayLine2} />
                  </div>
                </div>
              ) : (
                <div className={shelfStyle.normal}>
                  <div className={shelfStyle.firstLine} />
                  <div className={shelfStyle.secondLine} />
                  <div
                    className={shelfStyle.text}
                    dangerouslySetInnerHTML={{ __html: formatPrice(price, questionSchema.get('price_mask')) }}
                  />
                  <div className={shelfStyle.grayLine1} />
                  <div className={shelfStyle.grayLine2} />
                  <div className={shelfStyle.grayLine3} />
                </div>
              )}
            </Fragment>
          ),
          loblaws: (
            <Fragment>
              <div className={shelfStyle.leftLine} />
              <div className={shelfStyle.innerDiv}>
                <div className={`${shelfStyle.text} ` + (discounted ? `${shelfStyle.yellow}` : '')}>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: formatPrice(discounted ? discount : price, questionSchema.get('price_mask'))
                    }}
                  />
                </div>
                <div className={shelfStyle.firstLine} />
                <div className={shelfStyle.starLine} />
                <div className={shelfStyle.secondLine} />
                <div className={shelfStyle.thirdLine} />
                <div className={shelfStyle.thirdLine} />
              </div>
              <div className={shelfStyle.rightLine} />
            </Fragment>
          ),
          cstore: (
            <Fragment>
              {discounted ? (
                <div className={shelfStyle.discounted}>
                  <div className={shelfStyle.left}>
                    <div
                      className={shelfStyle.text}
                      dangerouslySetInnerHTML={{ __html: formatPrice(price, questionSchema.get('price_mask')) }}
                    />
                    <div className={shelfStyle.grayLine1} />
                    <div className={shelfStyle.grayLine2} />
                    <div className={shelfStyle.grayLine3} />
                  </div>
                  <div className={shelfStyle.right}>
                    <div
                      className={shelfStyle.text}
                      dangerouslySetInnerHTML={{
                        __html: formatPrice(discount, questionSchema.get('price_mask'))
                      }}
                    />
                    <div className={shelfStyle.grayLine1} />
                  </div>
                </div>
              ) : (
                <div className={shelfStyle.normal}>
                  <div className={shelfStyle.firstLine} />
                  <div className={shelfStyle.secondLine} />
                  <div
                    className={shelfStyle.text}
                    dangerouslySetInnerHTML={{ __html: formatPrice(price, questionSchema.get('price_mask'), true) }}
                  />
                  <div className={shelfStyle.grayLine1} />
                  <div className={shelfStyle.grayLine2} />
                </div>
              )}
            </Fragment>
          )
        }[type]
      }
    </div>
  )
}

ItemPrice.propTypes = {
  questionSchema: PropTypes.instanceOf(Map).isRequired,
  item: PropTypes.instanceOf(Map).isRequired,
  type: PropTypes.string,
  noRandom: PropTypes.bool,
  left: PropTypes.number
}

export default memo(
  ItemPrice,
  (prevProps, nextProps) =>
    prevProps.questionSchema === nextProps.questionSchema &&
    prevProps.item === nextProps.item &&
    prevProps.type === nextProps.type &&
    // the comparator used to compare questionSchema twice and skip these two rendered props
    prevProps.left === nextProps.left &&
    prevProps.noRandom === nextProps.noRandom
)
